import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, StatusPill } from "@ozpanel/kit";
import { Button, Section, StateView, useAuth, useToast } from "@ozpanel/ui";

// The platform's own sender — the address the panel's mail comes from (password resets, new logins,
// account alerts). It existed nowhere until 2026-10-20: that mail left as root@<node>, unauthenticated,
// and its bounces were lost. Set here, the cluster signs for the address's domain; the records below
// are published where that domain's DNS lives (often a registrar, not this cluster), and the page says
// what is still missing. Nothing reads "in force" until every record is published and this node signs.

interface PlatformRecord {
  type: string;
  name: string;
  value: string;
  found: string;
  status: "ok" | "missing" | "wrong" | "unknown";
  why?: string;
}
interface PlatformSender {
  address: string;
  domain?: string;
  selector?: string;
  records?: PlatformRecord[];
  this_node_signs?: boolean;
  signing_detail?: string;
  in_force: boolean;
  note: string;
}

const TONE: Record<PlatformRecord["status"], "ok" | "bad" | "warn" | "neutral"> = { ok: "ok", missing: "bad", wrong: "warn", unknown: "neutral" };
const inputCls = "min-w-[18rem] flex-1 rounded-md border border-neutral-300 px-2 py-1.5 font-mono text-[12.5px]";

export function PlatformSenderScreen() {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["platform-sender"], queryFn: () => client.apiFetch<PlatformSender>("/mail/platform-sender") });
  const [addr, setAddr] = React.useState("");
  const [to, setTo] = React.useState("");
  React.useEffect(() => {
    if (q.data && addr === "") setAddr(q.data.address);
  }, [q.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (address: string) => client.apiFetch<PlatformSender>("/mail/platform-sender", { method: "PUT", body: JSON.stringify({ address }) }),
    onSuccess: (d) => {
      qc.setQueryData(["platform-sender"], d);
      notify({ kind: "success", title: d.address ? "Platform sender set" : "Cleared — the panel's mail falls back to root@<node>" });
    },
    onError: (e: unknown) => notify({ kind: "error", title: "Not saved", body: e instanceof Error ? e.message : String(e) }),
  });
  const test = useMutation({
    mutationFn: (recipient: string) =>
      client.apiFetch<{ from: string; node: string; note: string }>("/mail/platform-sender/test", { method: "POST", body: JSON.stringify({ to: recipient }) }),
    onSuccess: (d) => notify({ kind: "success", title: `Handed to ${d.node}'s mail server, from ${d.from}`, body: d.note }),
    onError: (e: unknown) => notify({ kind: "error", title: "Test not sent", body: e instanceof Error ? e.message : String(e) }),
  });

  const d = q.data;
  return (
    <div>
      <PageHeader title="Platform sender" subtitle="The address the panel's own mail comes from — password resets, new logins, account alerts" />
      <StateView loading={q.isLoading} error={q.error} errorContext="the platform sender">
        {d && (
          <>
            <Section title="Address">
              <div className="flex flex-wrap items-center gap-2">
                <input aria-label="Platform sender address" className={inputCls} placeholder="notify@example.com" value={addr} onChange={(e) => setAddr(e.target.value)} />
                <Button size="sm" disabled={save.isPending} onClick={() => save.mutate(addr.trim())}>
                  {save.isPending ? "Saving…" : "Save"}
                </Button>
                {d.address && (
                  <Button size="sm" variant="ghost" disabled={save.isPending} onClick={() => { setAddr(""); save.mutate(""); }}>
                    Clear
                  </Button>
                )}
                {d.address ? (
                  d.in_force ? <StatusPill tone="ok">in force</StatusPill> : <StatusPill tone="warn">not in force yet</StatusPill>
                ) : (
                  <StatusPill tone="neutral">not set — falls back to root@&lt;node&gt;</StatusPill>
                )}
              </div>
              <p className="mt-2 max-w-3xl text-[12px] leading-relaxed text-muted">{d.note}</p>
              {d.address && (
                <p className="mt-1 max-w-3xl text-[11.5px] leading-relaxed text-muted">
                  Mail to {d.address} keeps going wherever its domain&apos;s MX points — make sure that address is a mailbox or
                  forward someone reads: replies and bounces arrive there.
                </p>
              )}
            </Section>

            {d.records && (
              <Section title="Records to publish" className="mt-6">
                <p className="mb-3 max-w-3xl text-[12px] leading-relaxed text-muted">
                  SPF, DKIM and DMARC go where {d.domain}&apos;s DNS is managed. PTR (reverse DNS) is set at the provider that rents each
                  address. Each row is checked against public DNS each time this page loads.
                </p>
                <div className="space-y-3">
                  {d.records.map((r) => (
                    <div key={r.type + r.name} className="rounded-lg border border-line p-3">
                      <div className="mb-1 flex flex-wrap items-center gap-2 text-[12.5px]">
                        <StatusPill tone={TONE[r.status]}>{r.status}</StatusPill>
                        <span className="font-medium">{r.type}</span>
                        <span className="font-mono">{r.name}</span>
                      </div>
                      {r.value && <pre className="whitespace-pre-wrap break-all rounded bg-neutral-100 p-2 font-mono text-[11.5px] dark:bg-neutral-800">{r.value}</pre>}
                      {r.found && r.status !== "ok" && (
                        <p className="mt-1 break-all text-[11.5px] text-muted">
                          Published now: <span className="font-mono">{r.found}</span>
                        </p>
                      )}
                      {r.why && <p className="mt-1 text-[11.5px] text-muted">{r.why}</p>}
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-[11.5px] text-muted">
                  This node signs: {d.this_node_signs ? "yes" : "no"}
                  {d.signing_detail ? ` — ${d.signing_detail}` : ""}
                </p>
              </Section>
            )}

            <Section title="Send a test" className="mt-6">
              <div className="flex flex-wrap items-center gap-2">
                <input aria-label="Send a test to" className={inputCls} placeholder="you@example.com" value={to} onChange={(e) => setTo(e.target.value)} />
                <Button size="sm" variant="ghost" disabled={test.isPending || !to.includes("@")} onClick={() => test.mutate(to.trim())}>
                  {test.isPending ? "Sending…" : "Send from this node"}
                </Button>
              </div>
              <p className="mt-2 max-w-3xl text-[11.5px] text-muted">
                The received message&apos;s headers show whether it was signed (DKIM-Signature) and how the receiver judged it
                (Authentication-Results).
              </p>
            </Section>
          </>
        )}
      </StateView>
    </div>
  );
}
